import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import api from '../api/client';
import { useVociPortale } from '../utils/vociPortale';
import useTicketDaLeggere from '../utils/useTicketDaLeggere';
import useAbbinamentiDaApprovare from '../utils/useAbbinamentiDaApprovare';
import useFipavDaValidare from '../utils/useFipavDaValidare';
import { LogOut, ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen, Repeat, ListTree } from 'lucide-react';
import { costruisciNav, useMenuStaff } from './menuStaff';
import { useVociAllenatoreStaff } from './menuPortale';

// Le voci (con i loro permessi) e il menu predefinito sono in menuStaff.js; l'ordine e i menu
// li decide ROOT da Impostazioni → Menu laterale (23/10/2026, prima era un trascinamento salvato
// solo nel browser). I permessi restano sui singoli figli: un gruppo sparisce solo se non resta
// nessun figlio visibile, quindi lo spostamento non cambia chi vede cosa.

export default function Sidebar({ mobile = false }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, logout } = useAuth();
  const voci = useVociPortale();
  // requirePermesso: stringa o array (basta uno dei moduli); ROOT vede sempre tutto
  const vedeModulo = (req) => user?.ruolo === 'ROOT' || (Array.isArray(req) ? req : [req]).some(mod => !!user?.permessi?.[mod]?.view);
  const { collapsed: collapsedPref, toggle } = useSidebar();
  const collapsed = collapsedPref && !mobile; // sul telefono il menu è sempre esteso
  const [open, setOpen]         = useState({});
  const layoutMenu = useMenuStaff();
  const vociAllenatoreStaff = useVociAllenatoreStaff();
  const [contrattiDaConfermare, setContrattiDaConfermare] = useState(0);
  const [rimborsiDaApprovare, setRimborsiDaApprovare] = useState(0);
  const ticketDaLeggere = useTicketDaLeggere(user?.ruolo === 'ROOT' || !!user?.permessi?.ticket?.view);
  const fipavDaValidare = useFipavDaValidare(user?.ruolo === 'ROOT' || !!user?.permessi?.fipav?.view);
  const abbinamentiDaApprovare = useAbbinamentiDaApprovare(user?.ruolo === 'ROOT' || !!user?.permessi?.anagrafica?.view);
  // contatori rossi per indirizzo, ovunque stia la voce (il gruppo mostra il pallino se un figlio ne ha)
  const badgeFigli = {
    '/dipendenti/rimborsi': rimborsiDaApprovare, '/anagrafica': abbinamentiDaApprovare,
    '/supporto': ticketDaLeggere, '/tornei-fipav': fipavDaValidare,
  };

  // Switch "modalità allenatore": per uno staff che è anche allenatore (es. Operatore+coach),
  // mostrare le voci da allenatore mescolate a quelle da staff creava confusione (vedi feedback
  // titolare 2026-10-01, caso Enrico Mautino) — ora stanno dietro un pulsante esplicito, con
  // preferenza ricordata per utente in localStorage.
  const puoSwitchare = user?.is_allenatore && user?.ruolo !== 'ALLENATORE';
  const [modalitaAllenatore, setModalitaAllenatore] = useState(
    () => puoSwitchare && localStorage.getItem(`modalita_allenatore_${user.id}`) === 'true'
  );
  const cambiaModalita = (v) => {
    setModalitaAllenatore(v);
    if (user?.id) localStorage.setItem(`modalita_allenatore_${user.id}`, String(v));
    navigate(v ? '/le-mie-presenze' : '/');
  };
  const SOLO_ALLENATORE_EXTRA = ['/cambia-password', '/password-manager', '/guida'];

  // Badge su "LE MIE PRESENZE": un allenatore con ruolo staff custom (es. Operatore+coach) non
  // atterra su /allenatore dopo il login (solo il ruolo letterale ALLENATORE ci va, vedi Login.js)
  // — senza un avviso qui rischia di non accorgersi mai di un contratto da confermare.
  useEffect(() => {
    if (!user?.is_allenatore) { setContrattiDaConfermare(0); return; }
    api.get('/dipendenti/me/contratti')
      .then(r => setContrattiDaConfermare(r.data.filter(c => c.stato !== 'FIRMATO').length))
      .catch(() => {});
  }, [user?.is_allenatore]);

  // Badge su "Rimborsi" (dipendenti): quante richieste sono IN_ATTESA di una decisione dello
  // staff — altrimenti restano invisibili finché qualcuno non apre la pagina per controllare.
  const puoVedereRimborsi = user?.ruolo === 'ROOT' || !!user?.permessi?.dipendenti?.view;
  useEffect(() => {
    if (!puoVedereRimborsi) { setRimborsiDaApprovare(0); return; }
    api.get('/rimborsi/conteggio')
      .then(r => setRimborsiDaApprovare(r.data.n || 0))
      .catch(() => {});
  }, [puoVedereRimborsi]);

  // Voci da allenatore in cima: per un puro ALLENATORE sempre (unica sidebar che ha); per uno
  // staff anche-allenatore solo quando ha attivato lo switch "modalità allenatore".
  const vociAllenatore = user?.is_allenatore && (!puoSwitchare || modalitaAllenatore)
    ? vociAllenatoreStaff.filter(v => voci.attivaPath(v.path, user)) : [];
  const orderedNav = [...vociAllenatore, ...costruisciNav(layoutMenu)];

  const toggleGroup = (id) => setOpen(p => ({ ...p, [id]: !p[id] }));
  const isActive    = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);
  const hasActive   = (children) => children?.some(c => isActive(c.path, c.exact));

  const W = mobile ? 260 : (collapsed ? 60 : 220);

  return (
    <div className="app-sidebar" style={{ ...s.sidebar, width: W, ...(mobile ? { zIndex:1001, boxShadow:'0 10px 40px rgba(0,0,0,.45)', overflowY:'auto' } : {}) }}>

      {/* Logo + toggle */}
      <div style={{ ...s.logoWrap, justifyContent: collapsed ? 'center' : 'space-between', paddingRight: collapsed ? 0 : '8px' }}>
        {!collapsed && (
          <div style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', flex:1, minWidth:0 }} onClick={() => navigate(modalitaAllenatore ? '/le-mie-presenze' : '/')}>
            <img src="/logo.png" alt="" style={s.logo} onError={e => { e.target.style.display='none'; }}/>
            <div style={s.brand}>B-CHEERS ASD</div>
          </div>
        )}
        <button style={mobile ? { display:'none' } : s.collapseBtn} onClick={toggle} title={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}>
          {collapsed
            ? <PanelLeftOpen  size={16} color="var(--text-dim)"/>
            : <PanelLeftClose size={16} color="var(--text-dim)"/>
          }
        </button>
      </div>

      <div style={s.divider}/>

      {puoSwitchare && (
        <button
          title={collapsed ? (modalitaAllenatore ? 'Torna a modalità staff' : 'Passa a modalità allenatore') : undefined}
          style={{
            display:'flex', alignItems:'center', justifyContent: collapsed ? 'center' : 'flex-start', gap:'8px',
            margin:'10px 8px 0', padding:'8px 10px', borderRadius:'7px', cursor:'pointer',
            border:'1px solid #F4600C40', background: modalitaAllenatore ? '#F4600C20' : 'transparent',
          }}
          onClick={() => cambiaModalita(!modalitaAllenatore)}
        >
          <Repeat size={14} color="#F4600C"/>
          {!collapsed && (
            <span style={{ fontSize:'var(--font-size-xs)', fontWeight:'700', letterSpacing:'0.5px', color:'#F4600C' }}>
              {modalitaAllenatore ? 'TORNA A MODALITÀ STAFF' : 'PASSA A MODALITÀ ALLENATORE'}
            </span>
          )}
        </button>
      )}

      <nav style={s.nav}>
        {orderedNav.map((item) => {
          const daAllenatore = vociAllenatore.includes(item);
          if (!daAllenatore && modalitaAllenatore && !item.children && !SOLO_ALLENATORE_EXTRA.includes(item.path)) {
            // In modalità allenatore nascondiamo tutto il resto dello staff, tranne pochi
            // elementi di utilità generale (cambio password, password manager, guida).
            return null;
          }
          // ROOT bypassa sempre (mai una mappa permessi); per tutti gli altri, un modulo
          // richiesto ma senza view nasconde la voce — il backend blocca comunque i dati,
          // questo evita solo di mostrare un link che porterebbe a una pagina vuota/403.
          if (item.requirePermesso && !vedeModulo(item.requirePermesso)) return null;
          if (item.children) {
            // Ogni figlio può dipendere da un modulo permesso diverso da quello dei fratelli
            // (es. Calendario dipendenti richiede "presenze", non "dipendenti") — si filtra la
            // lista dei figli e si nasconde l'intero gruppo solo se non ne resta visibile nessuno.
            const childrenVisibili = item.children.filter(c =>
              (!modalitaAllenatore || SOLO_ALLENATORE_EXTRA.includes(c.path)) &&
              (!c.requirePermesso || vedeModulo(c.requirePermesso))
            );
            if (childrenVisibili.length === 0) return null;
            const active = hasActive(childrenVisibili);
            const isOpen = !collapsed && (open[item.id] ?? active);
            const mostraBadgeGruppo = childrenVisibili.some(c => badgeFigli[c.path] > 0);
            return (
              <div key={item.id}>
                <button
                  title={collapsed ? item.label : undefined}
                  className="sidebar-nav-btn"
                  style={{ ...s.navItem, ...(active ? s.navGroup : {}), justifyContent: collapsed ? 'center' : 'flex-start' }}
                  onClick={() => {
                    if (collapsed) navigate(childrenVisibili[0].path);
                    else toggleGroup(item.id);
                  }}
                >
                  <span style={{ position:'relative', display:'inline-flex' }}>
                    <item.icon size={15} color={active ? '#F4600C' : 'var(--text-dim)'}/>
                    {mostraBadgeGruppo && (collapsed || !isOpen) && (
                      <span style={{ position:'absolute', top:-3, right:-3, width:7, height:7, borderRadius:'50%', background:'#EF4444' }}/>
                    )}
                  </span>
                  {!collapsed && <>
                    <span style={{ ...s.navLabel, color: active ? 'var(--text)' : 'var(--text-nav)', flex:1 }}>{item.label}</span>
                    {isOpen ? <ChevronDown size={12} color="var(--text-dim)"/> : <ChevronRight size={12} color="var(--text-dim)"/>}
                  </>}
                </button>
                {isOpen && (
                  <div className="sottomenu-apre" style={{ paddingLeft:'10px' }}>
                    {childrenVisibili.map(c => {
                      const cActive = location.pathname === c.path;
                      const numeroFiglio = badgeFigli[c.path] || 0;
                      return (
                        <button key={c.path}
                          className="sidebar-nav-btn"
                          style={{ ...s.navItem, ...s.navChild, ...(cActive ? s.navActive : {}) }}
                          onClick={() => navigate(c.path)}
                        >
                          <c.icon size={13} color={cActive ? '#F4600C' : 'var(--text-dim)'}/>
                          <span style={{ ...s.navLabel, color: cActive ? 'var(--text)' : 'var(--text-nav)', flex:1 }}>{c.label}</span>
                          {numeroFiglio > 0 && (
                            <span style={{ fontSize:'9px', fontWeight:'700', color:'#fff', background:'#EF4444', borderRadius:'8px', padding:'1px 6px', marginRight:'4px' }}>
                              {numeroFiglio}
                            </span>
                          )}
                          {cActive && <div style={s.activeBar}/>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const active = isActive(item.path, item.exact);
          const numeroBadge = item.path === '/le-mie-presenze/contratti' ? contrattiDaConfermare : (badgeFigli[item.path] || 0);
          const mostraBadgeContratti = numeroBadge > 0;
          return (
            <div key={item.path}>
              <button
                title={collapsed ? (mostraBadgeContratti ? `${item.label} — contratto da leggere` : item.label) : undefined}
                className="sidebar-nav-btn"
                style={{ ...s.navItem, ...(active ? s.navActive : {}), justifyContent: collapsed ? 'center' : 'flex-start', position: 'relative' }}
                onClick={() => navigate(item.path)}
              >
                <span style={{ position:'relative', display:'inline-flex' }}>
                  <item.icon size={15} color={active ? '#F4600C' : 'var(--text-dim)'}/>
                  {mostraBadgeContratti && collapsed && (
                    <span style={{ position:'absolute', top:-3, right:-3, width:7, height:7, borderRadius:'50%', background:'#EF4444' }}/>
                  )}
                </span>
                {!collapsed && <>
                  <span style={{ ...s.navLabel, color: active ? 'var(--text)' : 'var(--text-nav)' }}>{item.label}</span>
                  {mostraBadgeContratti && (
                    <span style={{ fontSize:'9px', fontWeight:'700', color:'#fff', background:'#EF4444', borderRadius:'8px', padding:'1px 6px', marginRight:'4px' }}>
                      {numeroBadge}
                    </span>
                  )}
                  {active && <div style={s.activeBar}/>}
                </>}
              </button>
            </div>
          );
        })}
      </nav>

      <div style={{ flexShrink:0 }}>
        <div style={s.divider}/>

        {!collapsed && (
          <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'14px 16px' }}>
            <div style={s.avatar}>{user?.nome?.[0] || 'A'}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'var(--font-size-sm)', fontWeight:'600', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.nome}</div>
              <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', fontWeight:'700' }}>{user?.ruolo}</div>
            </div>
            {user?.ruolo === 'ROOT' && (
              <button style={{ background:'transparent', border:'none', cursor:'pointer', padding:'4px', borderRadius:'4px', display:'flex', color:'var(--text-dim)' }}
                title="Modifica menu laterale" onClick={() => navigate('/settings?tab=menu')}>
                <ListTree size={14}/>
              </button>
            )}
          </div>
        )}
        {collapsed && (
          <div style={{ display:'flex', justifyContent:'center', padding:'14px 0' }}>
            <div style={s.avatar} title={user?.nome}>{user?.nome?.[0] || 'A'}</div>
          </div>
        )}
        <button style={{ ...s.logoutBtn, justifyContent: collapsed ? 'center' : 'flex-start' }}
          title={collapsed ? 'Esci' : undefined}
          onClick={() => { logout(); navigate('/login'); }}
        >
          <LogOut size={13} color="var(--text-dim)"/>
          {!collapsed && <span style={{ marginLeft:'8px' }}>ESCI</span>}
        </button>
      </div>
    </div>
  );
}

const s = {
  sidebar:    { height:'100vh', background:'var(--bg-sidebar)', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', position:'fixed', top:0, left:0, zIndex:100, transition:'width 0.2s ease' },
  logoWrap:   { padding:'14px 12px', display:'flex', alignItems:'center', gap:'8px', minHeight:'60px', flexShrink:0 },
  // Misura fissa: con il font di sistema (restyling) la misura legata allo schermo usciva dai 220px del menu
  brand:      { fontSize:'16px', fontWeight:'800', color:'#F4600C', letterSpacing:'0.5px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0 },
  logo:       { width:'28px', height:'28px', objectFit:'contain', filter:'brightness(0) invert(1)', flexShrink:0 },
  collapseBtn:{ background:'transparent', border:'none', cursor:'pointer', padding:'6px', borderRadius:'6px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  divider:    { height:'1px', background:'var(--border)', margin:'0 12px' },
  nav:        { padding:'12px 8px', display:'flex', flexDirection:'column', gap:'2px', flex:1, overflowY:'auto', overflowX:'hidden', minHeight:0 },
  navItem:    { display:'flex', alignItems:'center', gap:'9px', padding:'9px 12px', borderRadius:'7px', border:'none', background:'transparent', cursor:'pointer', position:'relative', width:'100%', textAlign:'left' },
  navGroup:   { background:'var(--nav-group-bg)' },
  navChild:   { padding:'7px 10px' },
  navActive:  { background:'var(--nav-active-bg)' },
  navLabel:   { fontSize:'var(--font-size-xs)', fontWeight:'700', letterSpacing:'0.5px', flex:1, whiteSpace:'nowrap' },
  activeBar:  { position:'absolute', right:0, top:'50%', transform:'translateY(-50%)', width:'3px', height:'18px', background:'#F4600C', borderRadius:'2px' },
  logoutBtn:  { display:'flex', alignItems:'center', padding:'10px 16px', background:'transparent', border:'none', cursor:'pointer', width:'100%', fontSize:'var(--font-size-xs)', letterSpacing:'1px', color:'var(--text-dim)', fontWeight:'700', marginBottom:'8px' },
  avatar:     { width:'32px', height:'32px', background:'#F4600C20', border:'1px solid #F4600C30', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize: 'var(--font-size-sm)', fontWeight:'700', color:'#F4600C', flexShrink:0 },
};
